import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedTaskUpdateUpdatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface TaskUpdateUpdatedEventV2Props {
  event: DiscriminatedTaskUpdateUpdatedEvent;
}

/**
 * V2 renderer for Task Update Updated events that uses the `previous` field
 * to show only what changed. Similar to StaffNoteUpdatedEventV2.
 */
export const TaskUpdateUpdatedEventV2: React.FC<
  TaskUpdateUpdatedEventV2Props
> = ({ event }) => {
  const { task_update, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
          <PencilIcon className="h-4 w-4 text-amber-600" />
        </div>
      }
    />
  );

  // Determine what changed
  const currentContent = task_update.update;
  const previousContent = previous?.update;
  const currentType = task_update.update_type;
  const previousType = previous?.update_type;
  const currentVisibility = task_update.visibility;
  const previousVisibility = previous?.visibility;

  const contentChanged =
    previousContent !== undefined && currentContent !== previousContent;
  const typeChanged =
    previousType !== undefined && currentType !== previousType;
  const visibilityChanged =
    previousVisibility !== undefined &&
    currentVisibility !== previousVisibility;

  const isSignificantContentChange =
    contentChanged &&
    Math.abs((currentContent?.length || 0) - (previousContent?.length || 0)) >
      50;

  const body = (
    <ActivityBody>
      {contentChanged && (
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-700">
            Updated content:
          </div>
          <ActivityDescription>
            {currentContent && currentContent.length > 200
              ? `${currentContent.substring(0, 200)}...`
              : currentContent}
          </ActivityDescription>
          {isSignificantContentChange && (
            <ActivityDescription>
              <span className="text-sm text-gray-500">
                (
                {currentContent.length > (previousContent?.length || 0)
                  ? '+'
                  : ''}
                {currentContent.length - (previousContent?.length || 0)}{' '}
                characters)
              </span>
            </ActivityDescription>
          )}
        </div>
      )}

      {typeChanged && (
        <ActivityDescription>
          <span className="font-medium">Update Type:</span>{' '}
          {formatEnumValue(currentType)}
        </ActivityDescription>
      )}

      {visibilityChanged && (
        <ActivityDescription>
          <span className="font-medium">Visibility:</span>{' '}
          {formatEnumValue(currentVisibility)}
        </ActivityDescription>
      )}

      {!contentChanged && !typeChanged && !visibilityChanged && (
        <ActivityDescription>
          <span className="text-sm text-gray-500 italic">
            No visible changes
          </span>
        </ActivityDescription>
      )}
    </ActivityBody>
  );

  const actions = task_update?.task_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/updates?task_id=${task_update.task_id}`}
      >
        View Task
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Task Update Modified"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
